/**
 * Admin Attributes — manage product attribute groups (size, material, fit, color, etc.).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, Save, Trash2, Loader2, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";

const BLANK = {
    _new: true,
    name: "",
    key: "",
    type: "select",
    description: "",
    is_active: true,
    sort_order: 0,
    options: [{ label: "", value: "", swatch: "", extra: "" }],
};

const TYPE_OPTIONS = [
    { value: "select", label: "Select (dropdown)" },
    { value: "color", label: "Color (swatch)" },
    { value: "text", label: "Free text" },
];

export default function AdminAttributes() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/attributes");
            setItems(data || []);
        } catch {
            toast.error("Failed to load attributes");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const update = (idx, patch) =>
        setItems((curr) => curr.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

    const updateOption = (idx, optIdx, patch) =>
        setItems((curr) =>
            curr.map((c, i) =>
                i === idx
                    ? {
                          ...c,
                          options: (c.options || []).map((o, j) => (j === optIdx ? { ...o, ...patch } : o)),
                      }
                    : c
            )
        );

    const addOption = (idx) =>
        setItems((curr) =>
            curr.map((c, i) =>
                i === idx
                    ? { ...c, options: [...(c.options || []), { label: "", value: "", swatch: "", extra: "" }] }
                    : c
            )
        );

    const removeOption = (idx, optIdx) =>
        setItems((curr) =>
            curr.map((c, i) =>
                i === idx ? { ...c, options: (c.options || []).filter((_, j) => j !== optIdx) } : c
            )
        );

    const addBlank = () =>
        setItems([{ ...BLANK, _tempId: Math.random().toString(36).slice(2) }, ...items]);

    const save = async (attr) => {
        try {
            const payload = {
                name: attr.name,
                key: attr.key || undefined,
                type: attr.type || "select",
                description: attr.description || "",
                is_active: !!attr.is_active,
                sort_order: attr.sort_order || 0,
                options: (attr.options || []).filter((o) => (o.label || "").trim()),
            };
            if (attr._new) {
                const { _tempId } = attr;
                const { data } = await api.post("/admin/attributes", payload);
                setItems((curr) => curr.map((x) => (x._tempId === _tempId ? data : x)));
                toast.success("Attribute created");
            } else {
                const { data } = await api.put(`/admin/attributes/${attr.id}`, payload);
                setItems((curr) => curr.map((x) => (x.id === attr.id ? data : x)));
                toast.success("Attribute saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (attr) => {
        if (attr._new) {
            setItems((curr) => curr.filter((x) => x._tempId !== attr._tempId));
            return;
        }
        if (!window.confirm(`Delete attribute "${attr.name}"?`)) return;
        try {
            await api.delete(`/admin/attributes/${attr.id}`);
            setItems((curr) => curr.filter((x) => x.id !== attr.id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const filtered = useMemo(
        () =>
            items.filter(
                (c) =>
                    !filter ||
                    (c.name || "").toLowerCase().includes(filter.toLowerCase()) ||
                    (c.key || "").toLowerCase().includes(filter.toLowerCase())
            ),
        [items, filter]
    );
    const { page, pageSize, pageItems, setPage, setPageSize } = usePagination(filtered, 8);

    return (
        <div className="space-y-6" data-testid="admin-attributes">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">Catalog</p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Attributes</h1>
                    <p className="text-xs text-[#857C72] mt-1">
                        Define product attributes — size charts, materials, fits, color swatches.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={addBlank}
                        className="dw-btn-primary inline-flex items-center gap-2 px-4 h-10 font-mono-ui text-xs"
                        data-testid="add-attribute-btn"
                    >
                        <Plus className="h-4 w-4" /> New attribute
                    </button>
                    <ExportButtons
                        rows={items}
                        filename="xplosiv_event_package_attributes"
                        columns={["name", "key", "type", "is_active", "sort_order"]}
                    />
                </div>
            </header>

            <input
                type="text"
                placeholder="Filter by name or key…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 rounded-full bg-white border border-[#E7DED2] text-sm max-w-xs"
                data-testid="attributes-filter"
            />

            {loading ? (
                <div className="flex items-center justify-center py-10 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
            ) : pageItems.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-[#E7DED2] p-10 text-center text-[#857C72]">
                    <SlidersHorizontal className="h-8 w-8 mx-auto mb-3 opacity-60" />
                    No attributes yet. Click <strong>New attribute</strong>.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {pageItems.map((c) => {
                        const idx = items.indexOf(c);
                        return (
                            <article
                                key={c.id || c._tempId}
                                className="rounded-2xl border border-[#E7DED2] bg-white p-5 space-y-3"
                                data-testid={`attribute-card-${c.key || idx}`}
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    <input
                                        placeholder="Name (e.g. Size)"
                                        value={c.name || ""}
                                        onChange={(e) => update(idx, { name: e.target.value })}
                                        className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-display"
                                    />
                                    <input
                                        placeholder="Key (auto from name)"
                                        value={c.key || ""}
                                        onChange={(e) => update(idx, { key: e.target.value })}
                                        className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-mono-ui"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-3 items-center">
                                    <select
                                        value={c.type || "select"}
                                        onChange={(e) => update(idx, { type: e.target.value })}
                                        className="px-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-xs"
                                    >
                                        {TYPE_OPTIONS.map((t) => (
                                            <option key={t.value} value={t.value}>
                                                {t.label}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="number"
                                        placeholder="Sort"
                                        value={c.sort_order ?? 0}
                                        onChange={(e) => update(idx, { sort_order: parseInt(e.target.value || "0", 10) })}
                                        className="px-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-xs"
                                    />
                                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={!!c.is_active}
                                            onChange={(e) => update(idx, { is_active: e.target.checked })}
                                        />
                                        Active
                                    </label>
                                </div>
                                <input
                                    placeholder="Description (admin only)"
                                    value={c.description || ""}
                                    onChange={(e) => update(idx, { description: e.target.value })}
                                    className="w-full px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-xs"
                                />
                                <div className="space-y-2 pt-2 border-t border-[#E7DED2]">
                                    <p className="text-[10px] font-mono-ui tracking-[0.15em] uppercase text-[#857C72]">
                                        Options ({(c.options || []).length})
                                    </p>
                                    <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                                        {(c.options || []).map((o, j) => (
                                            <div key={j} className="flex items-center gap-2" data-testid={`attribute-${idx}-option-${j}`}>
                                                <input
                                                    placeholder="Label (S, M, L…)"
                                                    value={o.label || ""}
                                                    onChange={(e) => updateOption(idx, j, { label: e.target.value })}
                                                    className="flex-1 px-3 py-1.5 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-xs"
                                                />
                                                <input
                                                    placeholder="Value (slug)"
                                                    value={o.value || ""}
                                                    onChange={(e) => updateOption(idx, j, { value: e.target.value })}
                                                    className="w-28 px-3 py-1.5 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-xs font-mono-ui"
                                                />
                                                {c.type === "color" && (
                                                    <input
                                                        type="text"
                                                        placeholder="#hex"
                                                        value={o.swatch || ""}
                                                        onChange={(e) => updateOption(idx, j, { swatch: e.target.value })}
                                                        className="w-20 px-3 py-1.5 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-xs font-mono-ui"
                                                    />
                                                )}
                                                {c.type === "color" && o.swatch && (
                                                    <span
                                                        className="h-6 w-6 rounded-full border border-[#E7DED2] flex-shrink-0"
                                                        style={{ background: o.swatch }}
                                                    />
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => removeOption(idx, j)}
                                                    className="h-7 w-7 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                                                    title="Remove"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => addOption(idx)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-[11px] font-mono-ui"
                                        data-testid={`attribute-add-option-${idx}`}
                                    >
                                        <Plus className="h-3 w-3" /> Add option
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 pt-2 border-t border-[#E7DED2]">
                                    <button
                                        onClick={() => save(c)}
                                        className="dw-btn-primary inline-flex items-center gap-1.5 px-4 h-9 text-xs font-mono-ui"
                                        data-testid={`attribute-save-${idx}`}
                                    >
                                        <Save className="h-3.5 w-3.5" /> Save
                                    </button>
                                    <button
                                        onClick={() => remove(c)}
                                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 text-xs font-mono-ui"
                                        data-testid={`attribute-delete-${idx}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
            <Pagination page={page} pageSize={pageSize} total={filtered.length} onPage={setPage} onPageSize={setPageSize} />
        </div>
    );
}
